import { Box, Collapse, FormControl, useToken } from "@chakra-ui/react";
import { GeoJsonGeometryTypes } from "geojson";
import { useEffect, useRef } from "react";
import { useIntl } from "react-intl";

import { VECTOR_LAYER_ID } from "@/config/map-layer-constants";
import { ProcessRunFormEnumSelect } from "@/features/project-process-run-form-enum-select";
import { RanaMap } from "@/lib/rana-map";
import {
  RanaMapPmTileLayerProps,
  getPmTileLayerType,
} from "@/lib/rana-map/components/rana-map-layers";

import { useGetVectorFileForSelector } from "./hooks/use-get-vector-file-for-selector";
import { VectorLayerSelectorFieldProps } from "./types/vector-layer-selector-field-props-type";

function getLayerProps(
  layer: string,
  type: string,
  color: string
): Partial<RanaMapPmTileLayerProps> {
  const pmTileLayerType = getPmTileLayerType(type as GeoJsonGeometryTypes);

  switch (pmTileLayerType) {
    case "circle":
      return {
        circleProps: {
          id: layer,
          paint: { "circle-color": color },
        },
      };
    case "line":
      return {
        lineProps: {
          id: layer,
          paint: { "line-color": color },
        },
      };
    case "fill":
    default:
      return {
        fillProps: {
          id: layer,
          paint: { "fill-color": color },
        },
      };
  }
}

export function ProcessRunFormVectorLayerSelector(props: VectorLayerSelectorFieldProps) {
  const { formData, onChange, name } = props;
  const { bounds, isLoading, layers, layerOptions, file } = useGetVectorFileForSelector(props);

  const [selectedColor] = useToken("colors", ["map.selected"]);

  const previousFileRef = useRef(file);
  // Auto-select the first layer when file changes or no value is set
  useEffect(() => {
    const fileChanged = previousFileRef.current !== file;

    if (layerOptions.length > 0 && (fileChanged || !formData)) {
      // We cannot use a callback from the parent because of the json schema parsing
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
      onChange(layerOptions[0].id);
      previousFileRef.current = file;
    }
  }, [layerOptions, formData, onChange, file]);

  const noLayerOrFileYet = !file || layerOptions.length === 0;

  const selectedLayerName = formData || layerOptions[0]?.id;
  const enumOptions = layerOptions.map((option) => ({
    value: option.id,
    label: option.name,
  }));

  return (
    <>
      <Collapse unmountOnExit in={noLayerOrFileYet && !isLoading}>
        <NoLayerOrFileYet hasFile={!!file} />
      </Collapse>
      <Collapse in={!noLayerOrFileYet}>
        <Box mb={4}>
          <ProcessRunFormEnumSelect<string>
            value={formData}
            options={{ enumOptions }}
            label=""
            id={props.id + "-select"}
            {...props}
          />
        </Box>

        {/* Map visualization */}
        <Box position="relative" h="md">
          <RanaMap mapId={name} isLoading={isLoading} bounds={bounds} mapProps={{}}>
            <RanaMap.MapScale />

            {/* Only show the selected layer */}
            {layers
              .filter(({ layer }) => layer === selectedLayerName)
              .map(({ layer, url, type }) => {
                if (!type) return null;
                const layerProps = getLayerProps(layer, type, selectedColor);

                return (
                  <RanaMap.Source
                    bounds={bounds}
                    id={layer}
                    key={layer}
                    type="vector"
                    url={`pmtiles://${url}`}
                  >
                    <RanaMap.PmTileLayer
                      source-layer={VECTOR_LAYER_ID}
                      type={type}
                      isVisible={true}
                      {...layerProps}
                    />
                  </RanaMap.Source>
                );
              })}
          </RanaMap>
        </Box>
      </Collapse>
    </>
  );
}

const NoLayerOrFileYet = ({ hasFile }: { hasFile: boolean }) => {
  const intl = useIntl();
  const message = !hasFile
    ? intl.formatMessage({
        id: "process.vector.layer.select_file_first",
        defaultMessage: "Please select a vector file first",
      })
    : intl.formatMessage({
        id: "process.vector.layer.no_layers_available",
        defaultMessage: "No layers available in the selected file",
      });

  return (
    <FormControl>
      <Box color="brand.grey.500" fontSize="sm">
        {message}
      </Box>
    </FormControl>
  );
};
